import { Component, lazy, Suspense, useEffect, type ComponentType, type ErrorInfo, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { bootstrap, queryClient, useAuth } from './lib/api';
import { isStaleBuildError, reloadForNewBuild, reportRenderError } from './lib/staleBuild';
import { ErrorState, GlobalFetching, LinkButton, Skeleton, ToastProvider } from './ui';
import LandlordShell from './shell/LandlordShell';
import ToFirstProperty from './shell/ToFirstProperty';
import { LegacyHandoverRedirect, LegacyTenancyRedirect, MessagesRedirect, RedirectKeepQuery, TenancyInUnit } from './shell/Redirects';

const Login = lazy(() => import('./routes/auth/Login'));
const MagicLogin = lazy(() => import('./routes/auth/MagicLogin'));
const VerifyEmail = lazy(() => import('./routes/auth/VerifyEmail'));
const Onboarding = lazy(() => import('./routes/auth/Onboarding'));
const AcceptInvite = lazy(() => import('./routes/auth/AcceptInvite'));
const Dashboard = lazy(() => import('./routes/landlord/Dashboard'));
const Properties = lazy(() => import('./routes/landlord/Properties'));
const PropertyDetail = lazy(() => import('./routes/landlord/PropertyDetail'));
const PropertyUnits = lazy(() => import('./routes/landlord/PropertyDetail').then((m) => ({ default: m.PropertyUnits })));
const PropertySectionRoute = lazy(() => import('./routes/landlord/PropertyDetail').then((m) => ({ default: m.PropertySectionRoute })));
const PropertyTenants = lazy(() => import('./routes/landlord/PropertyTenants'));
const PropertyCreate = lazy(() => import('./routes/landlord/PropertyCreate'));
const PropertyYearFile = lazy(() => import('./routes/landlord/PropertyYearFile'));
const PropertyChronicle = lazy(() => import('./routes/landlord/PropertyChronicle'));
const PropertyPeriods = lazy(() => import('./routes/landlord/PropertyPeriods'));
const PropertyMeters = lazy(() => import('./routes/landlord/PropertyMeters'));
const PropertyHoa = lazy(() => import('./routes/landlord/PropertyHoa'));
const PropertyUmlage = lazy(() => import('./routes/landlord/PropertyUmlage'));
const UnitCreate = lazy(() => import('./routes/landlord/UnitCreate'));
const UnitDetail = lazy(() => import('./routes/landlord/UnitDetail'));
const TenancyDetail = lazy(() => import('./routes/landlord/TenancyDetail'));
const HandoverEditor = lazy(() => import('./routes/landlord/HandoverEditor'));
const TenancyAccount = lazy(() => import('./routes/landlord/TenancyAccount'));
const Dunning = lazy(() => import('./routes/landlord/Dunning'));
const Deposits = lazy(() => import('./routes/landlord/Deposits'));
const Import = lazy(() => import('./routes/landlord/Import'));
const Persons = lazy(() => import('./routes/landlord/Persons'));
const Documents = lazy(() => import('./routes/landlord/Documents'));
const DocumentReview = lazy(() => import('./routes/landlord/DocumentReview'));
const Periods = lazy(() => import('./routes/landlord/Periods'));
const PeriodDetail = lazy(() => import('./routes/landlord/PeriodDetail'));
const PeriodChecklist = lazy(() => import('./routes/landlord/billing/PeriodChecklist'));
const Heating = lazy(() => import('./routes/landlord/billing/Heating'));
const Co2 = lazy(() => import('./routes/landlord/billing/Co2'));
const Correction = lazy(() => import('./routes/landlord/billing/Correction'));
const Dispatch = lazy(() => import('./routes/landlord/billing/Dispatch'));
const StatementPreview = lazy(() => import('./routes/landlord/billing/StatementPreview'));
const TaxExport = lazy(() => import('./routes/landlord/billing/TaxExport'));
const Payments = lazy(() => import('./routes/landlord/Payments'));
const MetersOverview = lazy(() => import('./routes/landlord/MetersOverview'));
const Deadlines = lazy(() => import('./routes/landlord/Deadlines'));
const Inbox = lazy(() => import('./routes/landlord/Inbox'));
const Settings = lazy(() => import('./routes/landlord/Settings'));
const Subscription = lazy(() => import('./routes/landlord/settings/Subscription'));
const SecuritySettings = lazy(() => import('./routes/landlord/settings/Security'));
const AccountData = lazy(() => import('./routes/landlord/settings/AccountData'));
const ForgotPassword = lazy(() => import('./routes/auth/ForgotPassword'));
const Cancel = lazy(() => import('./routes/auth/Cancel'));
const TenantLayout = lazy(() => import('./routes/tenant/TenantLayout'));
const TenantHome = lazy(() => import('./routes/tenant/TenantHome'));
const TenantStatement = lazy(() => import('./routes/tenant/TenantStatement'));
const TenantReadings = lazy(() => import('./routes/tenant/TenantReadings'));
const TenantReports = lazy(() => import('./routes/tenant/TenantReports'));
const TenantMessages = lazy(() => import('./routes/tenant/TenantMessages'));
const TenantLedger = lazy(() => import('./routes/tenant/TenantLedger'));
const TenantPrivacy = lazy(() => import('./routes/tenant/PersonalData'));

/**
 * Error boundary per route: a render error never becomes a white screen (Fallstricke). Keyed by the path, so the error
 * view belongs to the page that failed and the next navigation renders normally again.
 */
function Boundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <RouteBoundary key={pathname}>{children}</RouteBoundary>;
}

class RouteBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  override componentDidCatch(error: Error, info: ErrorInfo) {
    // an old tab after a deploy: load the new build instead of showing an error
    if (isStaleBuildError(error) && reloadForNewBuild()) return;
    reportRenderError(error, info.componentStack);
  }
  override render() {
    if (this.state.error)
      return <BoundaryFallback error={this.state.error} onRetry={() => (isStaleBuildError(this.state.error) ? window.location.reload() : this.setState({ error: null }))} />;
    return this.props.children;
  }
}

function BoundaryFallback({ error, onRetry }: { error: Error; onRetry: () => void }) {
  const { t } = useTranslation();
  return <ErrorState error={error} title={t('app.boundary.title')} onRetry={onRetry} />;
}

export function App() {
  const ready = useAuth((s) => s.ready);
  useEffect(() => void bootstrap(), []);
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrowserRouter>
          <GlobalFetching />
          {!ready ? (
            <div className="auth-page" aria-busy="true"><div className="auth-card"><Skeleton count={3} h={24} /></div></div>
          ) : (
            <Suspense fallback={<div className="main"><Skeleton count={6} h={24} /></div>}>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/passwort-vergessen" element={<ForgotPassword />} />
                {/* § 312k BGB Kündigungsbutton: public, no login (docs/BILLING.md §12) */}
                <Route path="/kuendigen" element={<Cancel />} />
                <Route path="/registrieren" element={<Login mode="signup" />} />
                <Route path="/mieter/login" element={<MagicLogin />} />
                {/* link of our confirmation mail (services/verificationMail.ts): public, works without a session */}
                <Route path="/email-bestaetigen" element={<VerifyEmail />} />
                <Route path="/einladung/:code" element={<RequireAuth><AcceptInvite /></RequireAuth>} />
                <Route path="/start" element={<RequireAuth><Onboarding /></RequireAuth>} />
                <Route path="/mieter" element={<RequireAuth><TenantLayout /></RequireAuth>}>
                  <Route index element={<Boundary><TenantHome /></Boundary>} />
                  <Route path="abrechnungen/:id" element={<Boundary><TenantStatement /></Boundary>} />
                  <Route path="zaehler" element={<Boundary><TenantReadings /></Boundary>} />
                  <Route path="meldungen" element={<Boundary><TenantReports /></Boundary>} />
                  <Route path="nachrichten" element={<Boundary><TenantMessages /></Boundary>} />
                  <Route path="konto" element={<Boundary><TenantLedger /></Boundary>} />
                  <Route path="daten" element={<Boundary><TenantPrivacy /></Boundary>} />
                  {/* "Übergabeprotokoll erhalten" notifications link the contract view of the app; the web lists it on the start page */}
                  <Route path="vertrag" element={<Navigate to="/mieter" replace />} />
                </Route>
                {/* Focus screens without sidebar (design/BelegDetail, design/AbrechnungVorschau) */}
                <Route path="/belege/:id" element={<RequireAuth><RequireLandlord><Boundary><DocumentReview /></Boundary></RequireLandlord></RequireAuth>} />
                <Route path="/abrechnungen/:id/mieter/:tenancyId" element={<RequireAuth><RequireLandlord><Boundary><StatementPreview /></Boundary></RequireLandlord></RequireAuth>} />
                <Route path="/objekte/neu" element={<RequireAuth><RequireLandlord><Boundary><PropertyCreate /></Boundary></RequireLandlord></RequireAuth>} />
                <Route element={<RequireAuth><RequireLandlord><LandlordShell /></RequireLandlord></RequireAuth>}>
                  <Route index element={<Boundary><Dashboard /></Boundary>} />
                  <Route path="objekte" element={<Boundary><Properties /></Boundary>} />
                  <Route path="objekte/:id" element={<Boundary><PropertyDetail /></Boundary>} />
                  <Route path="objekte/:id/einheiten" element={<Boundary><PropertyUnits /></Boundary>} />
                  <Route path="objekte/:id/vertraege" element={<Boundary><PropertyTenants /></Boundary>} />
                  <Route path="objekte/:id/belege" element={<Boundary><Scoped page={Documents} /></Boundary>} />
                  <Route path="objekte/:id/zahlungen" element={<Boundary><Scoped page={Payments} /></Boundary>} />
                  <Route path="objekte/:id/abrechnungen" element={<Boundary><Scoped page={Periods} /></Boundary>} />
                  <Route path="objekte/:id/kosten" element={<Boundary><PropertySectionRoute section="kosten" /></Boundary>} />
                  <Route path="objekte/:id/import" element={<Boundary><PropertySectionRoute section="import" /></Boundary>} />
                  <Route path="objekte/:id/einstellungen" element={<Boundary><PropertySectionRoute section="einstellungen" /></Boundary>} />
                  <Route path="objekte/:id/akte" element={<Boundary><PropertyYearFile /></Boundary>} />
                  <Route path="objekte/:id/chronik" element={<Boundary><PropertyChronicle /></Boundary>} />
                  <Route path="objekte/:id/zeitraeume" element={<Boundary><PropertyPeriods /></Boundary>} />
                  <Route path="objekte/:id/zaehler" element={<Boundary><PropertyMeters /></Boundary>} />
                  <Route path="objekte/:id/hausgeld" element={<Boundary><PropertyHoa /></Boundary>} />
                  <Route path="objekte/:id/umlage" element={<Boundary><PropertyUmlage /></Boundary>} />
                  <Route path="objekte/:id/einheiten/neu" element={<Boundary><UnitCreate /></Boundary>} />
                  {/* level 3: a unit and its tabs; a tenancy opens inside its unit (docs/09-screens.md "Navigation") */}
                  <Route path="einheiten/:id" element={<Boundary><UnitDetail /></Boundary>} />
                  <Route path="einheiten/:id/:tab" element={<Boundary><UnitDetail /></Boundary>} />
                  <Route path="einheiten/:unitId/mietverhaeltnisse/:id" element={<Boundary><TenancyInUnit><TenancyDetail /></TenancyInUnit></Boundary>} />
                  <Route path="einheiten/:unitId/mietverhaeltnisse/:id/konto" element={<Boundary><TenancyInUnit><TenancyAccount /></TenancyInUnit></Boundary>} />
                  <Route path="einheiten/:unitId/mietverhaeltnisse/:id/anpassen" element={<Boundary><TenancyInUnit><TenancyAccount adjust /></TenancyInUnit></Boundary>} />
                  <Route path="einheiten/:unitId/mietverhaeltnisse/:id/mahnung" element={<Boundary><TenancyInUnit><Dunning /></TenancyInUnit></Boundary>} />
                  <Route path="einheiten/:unitId/mietverhaeltnisse/:id/kaution" element={<Boundary><TenancyInUnit><Deposits /></TenancyInUnit></Boundary>} />
                  {/* Übergabeprotokoll (ADR-0025): editor of a draft, read-only view of a completed protocol */}
                  <Route path="einheiten/:unitId/mietverhaeltnisse/:id/uebergabe/:handoverId" element={<Boundary><TenancyInUnit><HandoverEditor /></TenancyInUnit></Boundary>} />
                  <Route path="einheiten/:unitId/mietverhaeltnisse/:id/:tab" element={<Boundary><TenancyInUnit><TenancyDetail /></TenancyInUnit></Boundary>} />
                  {/* old tenancy links: replaced by the nested route once the tenancy's unit is known */}
                  <Route path="mietverhaeltnisse/:id" element={<Boundary><LegacyTenancyRedirect /></Boundary>} />
                  <Route path="mietverhaeltnisse/:id/uebergabe/:handoverId" element={<Boundary><LegacyHandoverRedirect /></Boundary>} />
                  <Route path="mietverhaeltnisse/:id/:sub" element={<Boundary><LegacyTenancyRedirect /></Boundary>} />
                  <Route path="personen" element={<Boundary><Persons /></Boundary>} />
                  <Route path="belege" element={<Boundary><Documents /></Boundary>} />
                  <Route path="abrechnungen" element={<Boundary><Periods /></Boundary>} />
                  <Route path="abrechnungen/:id" element={<Boundary><PeriodDetail /></Boundary>} />
                  <Route path="abrechnungen/:id/checkliste" element={<Boundary><PeriodChecklist /></Boundary>} />
                  <Route path="abrechnungen/:id/heizkosten" element={<Boundary><Heating /></Boundary>} />
                  <Route path="abrechnungen/:id/co2" element={<Boundary><Co2 /></Boundary>} />
                  <Route path="abrechnungen/:id/korrektur" element={<Boundary><Correction /></Boundary>} />
                  <Route path="abrechnungen/:id/versand" element={<Boundary><Dispatch /></Boundary>} />
                  <Route path="export/steuer" element={<Boundary><TaxExport /></Boundary>} />
                  <Route path="zahlungen" element={<Boundary><Payments /></Boundary>} />
                  <Route path="zaehler" element={<Boundary><MetersOverview /></Boundary>} />
                  <Route path="chronik" element={<Boundary><ToFirstProperty tab="chronik" /></Boundary>} />
                  <Route path="fristen" element={<Boundary><Deadlines /></Boundary>} />
                  <Route path="import" element={<Boundary><Import /></Boundary>} />
                  <Route path="mitteilungen" element={<Boundary><Inbox /></Boundary>} />
                  <Route path="posteingang" element={<RedirectKeepQuery to="/mitteilungen" />} />
                  <Route path="meldungen/*" element={<Navigate to="/mitteilungen?ansicht=meldungen" replace />} />
                  <Route path="nachrichten" element={<MessagesRedirect />} />
                  <Route path="einstellungen" element={<Boundary><Settings /></Boundary>} />
                  <Route path="einstellungen/abo" element={<Boundary><Subscription /></Boundary>} />
                  <Route path="einstellungen/sicherheit" element={<Boundary><SecuritySettings /></Boundary>} />
                  <Route path="einstellungen/daten" element={<Boundary><AccountData /></Boundary>} />
                  <Route path="abo" element={<RedirectKeepQuery to="/einstellungen/abo" />} />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          )}
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  );
}

/** Account-wide list page shown for one property (/objekte/:id/belege …): the page gets the property id of the route. */
function Scoped({ page: Page }: { page: ComponentType<{ propertyId?: string }> }) {
  const { id } = useParams();
  return <Page key={id} propertyId={id} />;
}

function RequireAuth({ children }: { children: ReactNode }) {
  const token = useAuth((s) => s.token);
  const loc = useLocation();
  if (!token) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return <>{children}</>;
}

/** Roles decide the navigation tree (ADR-0006): tenants without landlord membership go to /mieter. */
function RequireLandlord({ children }: { children: ReactNode }) {
  const me = useAuth((s) => s.me);
  if (!me) return null;
  if (!me.memberships.length) return <Navigate to={me.tenant ? '/mieter' : '/start'} replace />;
  return <>{children}</>;
}

function NotFound() {
  const { t } = useTranslation();
  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1>{t('app.notFound.title')}</h1>
        <p className="muted">{t('app.notFound.text')}</p>
        <LinkButton to="/">{t('app.notFound.home')}</LinkButton>
      </div>
    </div>
  );
}
